// src/components/DividedVotes.jsx
// Divided votes: the year's recorded votes where at least one member voted no,
// with the arguments each side actually made.
//
// This is the factual record, not a forecast — nearly every vote this board
// takes is unanimous, so the handful that split are where the disagreement
// actually lives, and a bare category ("Health") says nothing about why.
// Each entry therefore carries the full bill title, what the ordinance does,
// who voted no by name, and the for/against arguments the transcript captured.
//
// Debate text comes from Meetings.json_data agenda_items[].summary.debate,
// which the analysis pipeline extracts per meeting. A bill is usually argued
// at its first reading and then passed at a later meeting, so the debate for
// "Finally pass X" lives on an earlier date — the matcher searches back
// across meetings rather than only the day of the vote.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAllRows } from '../utils/fetchAllRows';
import './DividedVotes.css';

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return String(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

const parseTally = (outcome) => {
  const m = String(outcome || '').match(/^(\d+)\s*-\s*(\d+)$/);
  if (!m) return null;
  return { yes: Number(m[1]), no: Number(m[2]) };
};

const parseVotes = (v) => {
  let arr = v;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch { arr = []; }
  }
  return Array.isArray(arr) ? arr : [];
};

/* ── Matching a vote to the meeting where it was argued ── */
const STOP = new Set([
  'the', 'and', 'of', 'to', 'a', 'an', 'for', 'on', 'in', 'with', 'at', 'from',
  'item', 'items', 'pass', 'finally', 'adopt', 'approve', 'ordinance',
  'resolution', 'first', 'reading', 'amended', 'as', 'city', 'vote', 'second',
  'third', 'fourth',
]);
const toks = (s) =>
  String(s || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
    .filter((w) => w.length > 3 && !STOP.has(w))
    .map((w) => w.replace(/s$/, ''));

function matchDebate(vote, debated) {
  const vt = toks(vote.issue);
  if (!vt.length) return null;
  const scored = [];
  for (const c of debated) {
    if (c.date > vote.voteDate) continue;
    const ct = toks(c.title);
    let hits = 0;
    for (const t of vt) {
      if (ct.some((w) => w === t || (w.length > 4 && t.length > 4 && (w.startsWith(t) || t.startsWith(w))))) {
        hits += 1;
      }
    }
    scored.push({ ...c, score: hits / vt.length });
  }
  if (!scored.length) return null;
  const top = Math.max(...scored.map((s) => s.score));
  if (top < 0.34) return null;
  // The same bill is often argued at several readings. Among near-best title
  // matches take the fullest argument record, then the one closest before the
  // vote — picking arbitrarily lands on a thin early mention instead.
  return scored
    .filter((s) => s.score >= top - 0.15)
    .sort((a, b) => (b.forArgs.length + b.againstArgs.length) - (a.forArgs.length + a.againstArgs.length)
      || b.date.localeCompare(a.date))[0];
}

function DividedVotes({ districtId, abbreviation }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [votings, setVotings] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [year, setYear] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [vRes, mRes] = await Promise.all([
        fetchAllRows(() => supabase.from('Votings')
          .select('id, voteDate, category, issue, outcome, votes')
          .eq('districtId', districtId)
          .order('voteDate', { ascending: false })
          .order('id', { ascending: false })),
        supabase.from('Meetings')
          .select('id, date, json_data')
          .eq('districtId', districtId)
          .order('date', { ascending: false })
          .limit(200),
      ]);
      if (cancelled) return;
      setVotings(vRes.error ? [] : vRes.data || []);
      setMeetings(mRes.error ? [] : mRes.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  // Every agenda item that captured an argument, flattened with its date.
  const debated = useMemo(() => {
    const out = [];
    for (const m of meetings) {
      let p = m.json_data;
      for (let i = 0; i < 2 && typeof p === 'string'; i += 1) {
        try { p = JSON.parse(p); } catch { p = null; }
      }
      for (const it of (p && p.agenda_items) || []) {
        const d = it && it.summary && it.summary.debate;
        const f = (d && Array.isArray(d.for) && d.for) || [];
        const a = (d && Array.isArray(d.against) && d.against) || [];
        if (!f.length && !a.length) continue;
        out.push({
          date: m.date,
          title: it.title || '',
          context: (it.summary && it.summary.context) || '',
          impact: (it.summary && it.summary.impact) || '',
          forArgs: f,
          againstArgs: a,
        });
      }
    }
    return out;
  }, [meetings]);

  const years = useMemo(() => {
    const s = new Set();
    for (const v of votings) {
      if (!parseTally(v.outcome)) continue;
      const y = String(v.voteDate || '').slice(0, 4);
      if (y) s.add(y);
    }
    return Array.from(s).sort().reverse();
  }, [votings]);

  useEffect(() => {
    if (!year && years.length) setYear(years[0]);
  }, [years, year]);

  const divided = useMemo(() => {
    if (!year) return [];
    return votings
      .map((v) => {
        const tally = parseTally(v.outcome);
        if (!tally || tally.no === 0) return null;
        if (String(v.voteDate || '').slice(0, 4) !== year) return null;
        const members = parseVotes(v.votes);
        const dissenters = members
          .filter((x) => String(x.vote).toLowerCase() === 'no')
          .map((x) => x.name)
          .filter(Boolean);
        return {
          vote: v,
          tally,
          dissenters,
          // Margin: how close the board came to splitting evenly. A 5-6 is far
          // more contested than a 10-1, and ordering by "no" count alone would
          // rank a lopsided 9-2 above a near-tie.
          margin: tally.yes - tally.no,
          debate: matchDebate(v, debated),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.margin - b.margin || b.vote.voteDate.localeCompare(a.vote.voteDate));
  }, [votings, debated, year]);

  const stats = useMemo(() => {
    const recorded = votings.filter(
      (v) => parseTally(v.outcome) && String(v.voteDate || '').slice(0, 4) === year
    ).length;
    // Who dissents most often — the board's recurring minority.
    const tally = new Map();
    for (const d of divided) {
      for (const name of d.dissenters) tally.set(name, (tally.get(name) || 0) + 1);
    }
    const byMember = Array.from(tally.entries()).sort((a, b) => b[1] - a[1]);
    return { recorded, withDebate: divided.filter((d) => d.debate).length, byMember };
  }, [votings, divided, year]);

  if (loading) return <div className="dv-card dv-status">Loading voting record…</div>;
  if (!divided.length) {
    return (
      <div className="dv-card dv-status">
        {years.length
          ? `Every recorded ${year} vote was unanimous — no divided votes to show.`
          : 'No recorded votes yet for this district.'}
      </div>
    );
  }

  return (
    <div className="dv-wrap">
      <div className="dv-card dv-head-card">
        <div className="dv-head-row">
          <div>
            <h3 className="dv-title">Where the board divided — {year}</h3>
            <p className="dv-sub">
              {divided.length} of {stats.recorded} recorded {year} votes drew at least
              one no. These are the decisions members disagreed on, with the
              arguments each side made.
            </p>
          </div>
          {years.length > 1 && (
            <label className="dv-year">
              <span>Year</span>
              <select value={year || ''} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
        </div>

        {stats.byMember.length > 0 && (
          <div className="dv-members">
            <span className="dv-members-label">Voted no most often:</span>
            {stats.byMember.slice(0, 6).map(([name, n]) => (
              <span key={name} className="dv-chip">
                {name} <strong>{n}</strong>
              </span>
            ))}
          </div>
        )}

        <p className="dv-note">
          Arguments are drawn from the meeting transcript for {stats.withDebate} of
          these {divided.length} votes; the rest were decided without recorded
          discussion, or their debate could not be matched to the vote. A bill is
          usually argued at its first reading and passed at a later meeting, so
          the arguments shown may come from an earlier date — each entry says
          which. Summaries are condensed from the recording and may be incomplete.
        </p>
      </div>

      {divided.map((d) => {
        const id = d.vote.id;
        const open = openId === id;
        return (
          <div className={`dv-card dv-item${open ? ' dv-item-open' : ''}`} key={id}>
            <div className="dv-item-head">
              <span className="dv-date">{fmtDate(d.vote.voteDate)}</span>
              <span className={`dv-tally${d.margin <= 1 ? ' dv-tally-close' : ''}`}>
                {d.tally.yes}–{d.tally.no}
              </span>
              {d.vote.category && <span className="dv-cat">{d.vote.category}</span>}
            </div>

            <h4 className="dv-issue">{d.vote.issue}</h4>

            {d.debate && d.debate.context && (
              <p className="dv-what">{d.debate.context}</p>
            )}

            {d.dissenters.length > 0 && (
              <p className="dv-dissent">
                <span className="dv-dissent-label">Voted no:</span>{' '}
                {d.dissenters.join(', ')}
              </p>
            )}

            {d.debate ? (
              <>
                <div className="dv-args">
                  <div className="dv-arg dv-arg-for">
                    <h5>Argued for</h5>
                    {d.debate.forArgs.length ? (
                      <ul>{d.debate.forArgs.map((a, i) => <li key={i}>{a}</li>)}</ul>
                    ) : (
                      <p className="dv-arg-none">No supporting argument was recorded.</p>
                    )}
                  </div>
                  <div className="dv-arg dv-arg-against">
                    <h5>Argued against</h5>
                    {d.debate.againstArgs.length ? (
                      <ul>{d.debate.againstArgs.map((a, i) => <li key={i}>{a}</li>)}</ul>
                    ) : (
                      <p className="dv-arg-none">No opposing argument was recorded.</p>
                    )}
                  </div>
                </div>
                <p className="dv-src">
                  {d.debate.date === d.vote.voteDate
                    ? 'From this meeting’s discussion.'
                    : `From the ${fmtDate(d.debate.date)} discussion of this bill.`}
                </p>
              </>
            ) : (
              <p className="dv-nodebate">
                No recorded discussion matched this vote — it may have been decided
                without debate, or bundled into a larger item.
              </p>
            )}

            <div className="dv-actions">
              <button
                type="button"
                className="dv-link"
                onClick={() => navigate(`?tab=voting#vote-${id}`)}
              >
                See the full roll call
              </button>
              {d.debate && d.debate.impact && (
                <button
                  type="button"
                  className="dv-link dv-link-quiet"
                  onClick={() => setOpenId(open ? null : id)}
                >
                  {open ? 'Hide what it changes' : 'What it changes'}
                </button>
              )}
            </div>

            {open && d.debate && d.debate.impact && (
              <p className="dv-impact">{d.debate.impact}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default DividedVotes;
